import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/cn';

/**
 * Oversized display link with a clear affordance: a persistent underline that
 * thickens on hover/focus, a trailing arrow that nudges, and a cursor hint.
 * Makes huge headline-style links obviously interactive.
 */
export function BigLink({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      data-cursor="hover"
      className={cn(
        'group inline-flex items-end gap-[0.15em] font-display leading-none tracking-tight transition-opacity',
        className,
      )}
    >
      <span className="relative inline-block pb-[0.08em]">
        {children}
        {/* persistent underline → reads as a link even at huge sizes */}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-ink/30 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:h-[6px] group-hover:bg-ink group-focus-visible:bg-ink"
        />
      </span>
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mb-[0.22em] inline-block h-[0.42em] w-[0.42em] text-brass transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1.5 group-hover:-translate-y-1"
      >
        <path d="M7 17 17 7M9 7h8v8" />
      </svg>
    </Link>
  );
}
